/**
 * Okno načtených měsíců kalendáře — souvislý úsek `[first, last]`, který roste, jak uživatel
 * doscrolluje k jeho hraně.
 *
 * Okno je jen **rozsah měsíců**; co je z něj zrovna vidět, řeší posuv v prohlížeči, a co je
 * načtené, řeší dotaz na každý měsíc zvlášť (`useCalendarMonthsData`). Díky tomu je dorůstání
 * okna levné: přibude pár měsíců na kraji a starší zůstanou v cache React Query.
 *
 * Okno má **strop** ({@link MAX_WINDOW_MONTHS}). Když doroste na hraně, na opačném konci se o tolik
 * měsíců zkrátí — jinak by dlouhé scrollování vyrobilo tisíce řádků a stovek dotazů a plocha by se
 * zadrhla. Zahozené měsíce nejsou ztracené: zůstávají v cache React Query, takže návrat zpátky je
 * levný. Posuv se přitom nesmí hnout, o to se stará korekce ve workspace.
 */
import { dayNumberOf, monthIndex, monthOfDay, shiftMonth, type MonthAnchor } from './days';

export interface CalendarWindow {
  first: MonthAnchor;
  last: MonthAnchor;
}

/** Kolik měsíců se načte před výchozím měsícem. */
export const INITIAL_MONTHS_BEFORE = 2;
/** Kolik měsíců se načte za výchozím měsícem. */
export const INITIAL_MONTHS_AFTER = 9;
/**
 * Nejvíc měsíců, které smí být najednou vykreslené. Strop je o výkonu: rozvrh se přepočítává přes
 * všechny řádky okna, takže s jeho délkou roste cena každé změny (donačtení, dopadlá data, přepnutí
 * režimu). Tři roky jsou pohodlná rezerva pro scrollování a zároveň řádově stovka řádků.
 */
export const MAX_WINDOW_MONTHS = 36;
/** O kolik měsíců okno povyroste při doscrollování k hraně. */
export const LOAD_CHUNK_MONTHS = 3;
/**
 * Jak daleko od okna se ještě vyplatí měsíce doplnit. Skok dál (deep-link do jiné sezony, „Dnes“
 * z opačného konce roku) okno **přesadí** — natahovat ho o desítky měsíců by znamenalo desítky
 * dotazů na data, kterými uživatel jen proletí.
 */
export const MAX_GAP_MONTHS = 6;

/** Okno kolem daného měsíce — výchozí stav i cíl přesazení. */
export function windowAround(month: MonthAnchor): CalendarWindow {
  return {
    first: shiftMonth(month, -INITIAL_MONTHS_BEFORE),
    last: shiftMonth(month, INITIAL_MONTHS_AFTER),
  };
}

/** Měsíce okna vzestupně. */
export function windowMonths(window: CalendarWindow): MonthAnchor[] {
  const count = monthIndex(window.last) - monthIndex(window.first) + 1;
  return Array.from({ length: Math.max(0, count) }, (_, index) => shiftMonth(window.first, index));
}

/** Rozsah dnů, který okno pokrývá (včetně obou krajů). */
export function windowRange(window: CalendarWindow): { start: number; end: number } {
  return {
    start: dayNumberOf(window.first.year, window.first.month, 1),
    // 0. den následujícího měsíce = poslední den měsíce předchozího
    end: dayNumberOf(window.last.year, window.last.month + 1, 0),
  };
}

/** Klíč okna — po jeho změně se uvolní pojistka proti opakovanému donačtení. */
export function windowKey(window: CalendarWindow): string {
  return `${monthIndex(window.first)}:${monthIndex(window.last)}`;
}

/** Délka okna v měsících. */
export function windowLength(window: CalendarWindow): number {
  return monthIndex(window.last) - monthIndex(window.first) + 1;
}

/** Ořízne okno na strop; `keep` říká, který kraj zůstane na místě. */
function clamp(window: CalendarWindow, keep: 'first' | 'last'): CalendarWindow {
  const over = windowLength(window) - MAX_WINDOW_MONTHS;
  if (over <= 0) return window;
  return keep === 'first'
    ? { first: window.first, last: shiftMonth(window.last, -over) }
    : { first: shiftMonth(window.first, over), last: window.last };
}

export function extendBefore(window: CalendarWindow, count = LOAD_CHUNK_MONTHS): CalendarWindow {
  return clamp({ ...window, first: shiftMonth(window.first, -count) }, 'first');
}

export function extendAfter(window: CalendarWindow, count = LOAD_CHUNK_MONTHS): CalendarWindow {
  return clamp({ ...window, last: shiftMonth(window.last, count) }, 'last');
}

/**
 * Okno, ve kterém daný měsíc leží. Blízko za hranou se okno natáhne (a měsíc dostane rezervu,
 * aby hned nespustil další donačtení), daleko se přesadí — viz {@link MAX_GAP_MONTHS}.
 */
export function windowFor(window: CalendarWindow, month: MonthAnchor): CalendarWindow {
  const target = monthIndex(month);
  const first = monthIndex(window.first);
  const last = monthIndex(window.last);
  if (target >= first && target <= last) return window;

  const gap = target < first ? first - target : target - last;
  if (gap > MAX_GAP_MONTHS) return windowAround(month);

  return target < first
    ? clamp({ ...window, first: shiftMonth(month, -1) }, 'first')
    : clamp({ ...window, last: shiftMonth(month, 1) }, 'last');
}

/** Okno, ve kterém leží daný den. */
export function windowForDay(window: CalendarWindow, day: number): CalendarWindow {
  const range = windowRange(window);
  if (day >= range.start && day <= range.end) return window;
  return windowFor(window, monthOfDay(Math.round(day)));
}
